import {useEffect, useMemo, useRef, useState} from 'react';

import {getLocalizationService} from '~/shared/services/localisationService';

type DefaultPayload = {
  scheduledDateTime?: number;
  scheduledDelta?: number;
  startingTimestamp?: number;
  isDataReady: boolean;
  isStaticEta?: boolean;
};

type UseDeliveryTrackerCounterResult = {
  isOverTwoHours: boolean;
  isExceeded: boolean;
  isMoreThan10MinutesLeft: boolean;
  isOver20Minutes: boolean;
  isToday: boolean;
  minutesToRender: number | string | null;
  isTrackerInitiated: boolean;
  totalEta: number;
};

const SECOND_IN_MILLIS = 1000;
const MINUTE_IN_MILLIS = 60 * SECOND_IN_MILLIS;
const MAX_TOTAL_ETA_IN_MINUTES = 120;
const TWO_HOURS_IN_MINUTES = 2 * 60;

const convertSecondsToMillis = (seconds: number) => seconds * SECOND_IN_MILLIS;

const getMinutesToRenderInTracker = ({minutesTimer, etaData, isOverTwoHours, isStaticEta}: {
  minutesTimer: number | null;
  etaData: {
    scheduledDateTime?: number;
    scheduledDelta?: number;
  };
  isOverTwoHours: boolean;
  isStaticEta?: boolean;
}) => {
  if (
    (!isOverTwoHours && !isStaticEta) ||
    typeof etaData.scheduledDateTime !== 'number' ||
    typeof etaData.scheduledDelta !== 'number'
  ) {
    return minutesTimer;
  }

  return new Date(convertSecondsToMillis(etaData.scheduledDateTime + etaData.scheduledDelta))
    .toLocaleTimeString('he', {hour: '2-digit', minute: '2-digit'});
};

const useDeliveryTrackerCounter = ({
  scheduledDateTime,
  scheduledDelta = 0,
  startingTimestamp = 0,
  isDataReady,
  isStaticEta,
}: DefaultPayload): UseDeliveryTrackerCounterResult => {
  // he format:
  // arrivalOrderDate: "30/06/2024" .toLocaleDateString('en-IL')
  // arrivalOrderTime: "13:30"

  // en format:
  // arrivalOrderDate: "6/30/2024" .toLocaleDateString('en')
  // arrivalOrderTime: "1:30 PM"

  const {currentLanguageKey} = getLocalizationService();
  const lastUsedScheduledDateTime = useRef<number | null>(null);
  const countdownStartingPoint = useRef<Date | null>(null);
  const [minutesTimer, setMinutesTimer] = useState<number | null>(null);
  const [isToday, setIsToday] = useState<boolean>(false);

  useEffect(() => {
    if (!scheduledDateTime || lastUsedScheduledDateTime.current === scheduledDateTime) {
      return;
    }

    if (!countdownStartingPoint.current || lastUsedScheduledDateTime.current !== scheduledDateTime) {
      lastUsedScheduledDateTime.current = scheduledDateTime;
      countdownStartingPoint.current = new Date();
    }

    const localeDateStringLangKey = currentLanguageKey === 'he' ? 'en-IL' : 'en';

    const arrivalOrderDateTimeToCompare = new Date(convertSecondsToMillis(scheduledDateTime + scheduledDelta));

    const isOrderDateIsToday =
      arrivalOrderDateTimeToCompare.toLocaleDateString(localeDateStringLangKey) ===
      countdownStartingPoint.current.toLocaleDateString(localeDateStringLangKey);

    setIsToday(isOrderDateIsToday);

    const arrivalOrderTimeInMinutes = (new Date(arrivalOrderDateTimeToCompare).getTime() - countdownStartingPoint.current.getTime()) / (1000 * 60);
    const minutesLeft = Math.round(arrivalOrderTimeInMinutes);
    setMinutesTimer(minutesLeft);
  }, [scheduledDateTime, scheduledDelta, currentLanguageKey]);

  useEffect(() => {
    const isCountDownRelevant = !minutesTimer || minutesTimer <= 0;
    if (isCountDownRelevant) {
      return;
    }

    const countDownInterval = setInterval(() => setMinutesTimer(_timer => (_timer ? _timer - 1 : _timer)), MINUTE_IN_MILLIS);

    return () => clearInterval(countDownInterval);
  }, [minutesTimer]);

  const totalEta = useMemo(() => {
    if (!startingTimestamp || !scheduledDateTime) {
      return 0;
    }

    const totalEtaInMinutes = (
      scheduledDateTime +
      scheduledDelta -
      startingTimestamp
    ) / 60;

    if (totalEtaInMinutes >= TWO_HOURS_IN_MINUTES) {
      return MAX_TOTAL_ETA_IN_MINUTES;
    }

    return totalEtaInMinutes;
  }, [scheduledDelta, scheduledDateTime, startingTimestamp]);

  const isOverTwoHours = typeof minutesTimer === 'number' && minutesTimer >= 120;

  return {
    isOverTwoHours,
    isExceeded: typeof minutesTimer === 'number' && minutesTimer <= 0,
    isMoreThan10MinutesLeft: typeof minutesTimer === 'number' && minutesTimer >= 10,
    isOver20Minutes: typeof minutesTimer === 'number' && minutesTimer <= -20,
    isToday,
    minutesToRender: getMinutesToRenderInTracker({
      minutesTimer,
      etaData: {scheduledDateTime, scheduledDelta},
      isOverTwoHours,
      isStaticEta,
    }),
    isTrackerInitiated: Boolean(isDataReady && minutesTimer),
    totalEta,
  };
};

export default useDeliveryTrackerCounter;
